import { initializeApp } from "firebase/app";
import {
  getFunctions,
  httpsCallable,
  connectFunctionsEmulator
} from "firebase/functions";

import {
  getAuth,
  signInAnonymously,
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  linkWithCredential,
  EmailAuthProvider,
  GoogleAuthProvider,
  linkWithPopup,
  signInWithPopup,
  signOut,
  sendPasswordResetEmail
  // connectAuthEmulator
} from "firebase/auth";

import {
  getFirestore,
  doc, addDoc, setDoc, getDoc, getDocs, updateDoc, deleteDoc, 
  serverTimestamp,
  collection,
  query,
  where,
  connectFirestoreEmulator,
  orderBy,
  limit,
  startAfter
} from "firebase/firestore";


const firebaseConfig = {
  apiKey: "AIzaSyBfuojzLGQ9Fo5KSgcCMnQDyE59MvbG-3o",
  authDomain: "mbsl001.firebaseapp.com",
  projectId: "mbsl001",
  storageBucket: "mbsl001.firebasestorage.app",
  messagingSenderId: "1065068598596",
  appId: "1:1065068598596:web:1a4c6e05ab8d84d7e7f311",
  measurementId: "G-2JVQ5DFFR3"
};


const app = initializeApp(firebaseConfig);
export const functions = getFunctions(app);
export const auth = getAuth(app);
export const db = getFirestore(app);

// if (window.location.hostname === "localhost") {
if (process.env.REACT_APP_USE_EMULATORS === "true") {
  connectFunctionsEmulator(functions, "localhost", 5001);
  connectFirestoreEmulator(db, "localhost", 8080);
  // connectAuthEmulator(auth, "localhost", 9099);
}

export const getHealthySwap = httpsCallable(functions, "getHealthySwap");
export const getSwapUsage = httpsCallable(functions, "getSwapUsage");
export const migrateSwaps = httpsCallable(functions, "migrateSwaps");

export const initAuth = () => {
  return new Promise((resolve, reject) => {
    const auth = getAuth();

    onAuthStateChanged(auth, async (user) => {
      if (user) {
        if (user.isAnonymous) {
          localStorage.setItem("anonUid", user.uid); // ✅ Save anon UID
        }
        resolve(user);
      } else {
        // Check if we had an anon UID stored before
        try {
          const cred = await signInAnonymously(auth);
          localStorage.setItem("anonUid", cred.user.uid);
          resolve(cred.user);
        } catch (err) {
          reject(err);
        }
      }
    });
  });
};

export const signupAndLink = async (email, password) => {
  const credential = EmailAuthProvider.credential(email, password);
  const user = auth.currentUser;

  if (user && user.isAnonymous) {
    const result = await linkWithCredential(user, credential);
    return result.user;
  } else {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    return result.user;
  }
};

export const signinWithEmail = async (email, password) => {
  const result = await signInWithEmailAndPassword(auth, email, password);
  return result.user;
};

export const upgradeWithGoogle = async () => {
  const provider = new GoogleAuthProvider();
  const user = auth.currentUser;

  try {
    const result = await linkWithPopup(user, provider);
    return result.user;
  } catch (error) {
    if (error.code === "auth/credential-already-in-use") {
      throw new Error("That Google account is already in use. Please sign in instead.");
    } else {
      throw error;
    }
  }
};

export const signinWithGoogle = async () => {
  const provider = new GoogleAuthProvider();
  const result = await signInWithPopup(auth, provider);
  return result.user;
};

export const logout = async () => {
  await signOut(auth);
};

export const saveUserProfile = async ({ uid, email, condition }) => {
  const userRef = doc(db, "users", uid);
  await setDoc(userRef, {
    email,
    condition,
    updatedAt: serverTimestamp(),
  }, { merge: true });
};

export const getUserProfile = async (uid) => {
  const userRef = doc(db, "users", uid);
  const snap = await getDoc(userRef);
  return snap.exists() ? snap.data() : null;
};

export const getUncuratedSwaps = async () => {
  const q = query(collection(db, "swapCache"), where("curated", "==", false));
  const snap = await getDocs(q);
  return snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

export const markSwapAsCurated = async (docId, userEmail) => {
  const docRef = doc(db, "swapCache", docId);
  await updateDoc(docRef, {
    curated: true,
    curatedAt: new Date().toISOString(),
    curatedBy: userEmail || "unknown"
  });
};

export const getUncuratedSwapsPaginated = async (lastDoc = null) => {
  let q = query(
    collection(db, "swapCache"),
    where("curated", "==", false),
    orderBy("createdAt", "desc"),
    limit(5)
  );

  if (lastDoc) {
    q = query(q, startAfter(lastDoc));
  }

  const snap = await getDocs(q);
  const swaps = snap.docs.map((doc) => ({
    id: doc.id,
    ...doc.data()
  }));

  return {
    swaps,
    lastDoc: snap.docs[snap.docs.length - 1] || null
  };
};

export const updateSwapCacheEntry = async (docId, data) => {
  const ref = doc(db, "swapCache", docId);
  await updateDoc(ref, data);
};

export const deleteSwapCacheEntry = async (docId) => {
  const ref = doc(db, "swapCache", docId);
  await deleteDoc(ref);
};

export const addFeedback = async ({ food, swap, explanation, feedback, user }) => {
  const db = getFirestore();
  await addDoc(collection(db, "feedback"), {
    food,
    swap,
    explanation,
    feedback,
    createdBy: user?.email || "anonymous",
    createdAt: serverTimestamp()
  });
};

export const resetPassword = async (email) => {
  const { auth } = await import("./firebase"); // or just use `auth` directly if already imported
  return await sendPasswordResetEmail(auth, email);
};

export const sendWelcomeEmail = async (payload) =>
  await httpsCallable(functions, "sendWelcomeEmail")(payload);